import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Home, Plus, Receipt, ShoppingCart } from 'lucide-react';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { hapticFeedback } from '../lib/telegram';
import styles from './TabBar.module.css';

interface Props {
  user: unknown;
}

export function TabBar(_: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handler = () => {
      setKeyboardVisible(window.innerHeight - vv.height > 100);
    };
    vv.addEventListener('resize', handler);
    return () => vv.removeEventListener('resize', handler);
  }, []);

  const { data: activeData } = useQuery({
    queryKey: queryKeys.projects.active,
    queryFn: () => api.getActiveProject().catch(() => ({ projectId: null })),
  });
  const activeProjectId = activeData?.projectId ?? null;

  const { data: projects = [] } = useQuery({
    queryKey: queryKeys.projects.all,
    queryFn: () => api.getProjects(),
  });
  const isFinanceProject =
    (projects.find((p) => p.id === activeProjectId)?.type ?? 'RENOVATION') === 'FINANCE';

  const { data: unreadReports } = useQuery({
    queryKey: queryKeys.reports.unreadCount(activeProjectId ?? ''),
    queryFn: ({ queryKey }) => api.getUnreadReportsCount(queryKey[1] as string),
    enabled: !!activeProjectId,
  });
  const hasUnreadReports = (unreadReports?.count ?? 0) > 0;

  const path = location.pathname;
  const isHome = path === '/';
  const isMaterials = /^\/projects\/[^/]+\/materials/.test(path);
  const isExpenses =
    /^\/projects\/[^/]+\/expenses(?:$|\/)/.test(path) && !/\/expenses\/new/.test(path);

  const go = (to: string, state?: Record<string, unknown>) => {
    hapticFeedback('light');
    navigate(to, state ? { state } : undefined);
  };

  const handleMaterials = () =>
    activeProjectId ? go(`/projects/${activeProjectId}/materials`) : go('/');

  const handleAddExpense = () =>
    activeProjectId ? go(`/projects/${activeProjectId}/expenses/new`, { entry: 'tabbar' }) : go('/');

  const handleExpenses = () =>
    activeProjectId ? go(`/projects/${activeProjectId}/expenses`) : go('/');

  if (keyboardVisible) return null;

  return (
    <nav className={styles.tabbar} aria-label={t('tabs.dashboard')}>
      <div className={styles.capsule}>
        <button
          type="button"
          className={`${styles.tab} ${isHome ? styles.active : ''}`}
          onClick={() => go('/')}
          aria-label={t('tabs.dashboard')}
          aria-current={isHome ? 'page' : undefined}
        >
          <Home size={22} strokeWidth={2} />
          {hasUnreadReports && <span className={styles.unreadDot} aria-hidden />}
        </button>

        {!isFinanceProject && (
          <button
            type="button"
            className={`${styles.tab} ${isMaterials ? styles.active : ''}`}
            onClick={handleMaterials}
            aria-label={t('tabs.collections')}
            aria-current={isMaterials ? 'page' : undefined}
          >
            <ShoppingCart size={22} strokeWidth={2} />
          </button>
        )}

        <button
          type="button"
          className={`${styles.tab} ${isExpenses ? styles.active : ''}`}
          onClick={handleExpenses}
          aria-label={t('tabs.expenses')}
          aria-current={isExpenses ? 'page' : undefined}
        >
          <Receipt size={22} strokeWidth={2} />
        </button>
      </div>

      <button
        type="button"
        className={styles.fab}
        onClick={() => { hapticFeedback('light'); handleAddExpense(); }}
        aria-label={t('expenses.add')}
      >
        <Plus size={26} strokeWidth={2.5} />
      </button>
    </nav>
  );
}
